import React from 'react';
import type { Migration } from '../../../types';
import { isInFlightStatus } from '../../../utils/migrationStatus';

interface DeleteModalContentProps {
  job: Migration;
}

// Rollback statuses: RollbackQueued=14 through RollbackFailed=20
// Backend may send numeric (14-20) or string name ('RollbackQueued', etc.)
const isRollbackStatus = (status: Migration['status']): boolean => {
  if (typeof status === 'string') return status.startsWith('Rollback');
  const n = typeof status === 'number' ? status : parseInt(String(status), 10);
  return !isNaN(n) && n >= 14 && n <= 20;
};

export const getMigrationTypeLabel = (job: Migration): string => {
  if (job.isSnapshot) return 'snapshot';
  if (isRollbackStatus(job.status)) return 'rollback';
  return 'migration';
};

export const DeleteModalContent: React.FC<DeleteModalContentProps> = ({ job }) => {
  const typeLabel = getMigrationTypeLabel(job);
  const inFlight = isInFlightStatus(job.status);
  return (
    <>
      <p className="text-base text-gray-700 dark:text-gray-300 text-center mb-1">
        Are you sure you want to delete{' '}
        <span className="font-semibold text-red-600 dark:text-red-500">{job.name}</span>?
      </p>
      <p className="text-sm text-blue-600 dark:text-blue-400 text-center">
        You will permanently delete the {typeLabel} from the RepliCloud Dashboard.
      </p>
      {inFlight && (
        <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
          <p className="text-sm text-amber-800 dark:text-amber-200 text-center font-semibold">
            This {typeLabel} is still running.
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300 text-center mt-1">
            Deleting it here removes it from the dashboard but does not stop work already in
            progress in Genesys Cloud, and any changes already applied will remain in the target
            org. Consider waiting for it to finish before deleting.
          </p>
        </div>
      )}
    </>
  );
};
